import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { plansAPI } from '../services/api';

// Module Card Component - extracted outside Dashboard
const ModuleCard = ({ module }) => (
  <Link
    to={module.link}
    className={`group relative bg-gradient-to-br ${module.gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
  >
    {module.badge && (
      <span className="absolute top-4 right-4 px-2 py-1 text-xs font-semibold bg-white/20 backdrop-blur-sm rounded-full">
        {module.badge}
      </span>
    )}
    
    <div className="flex items-start gap-4">
      <span className="text-4xl">{module.icon}</span>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-bold mb-1">{module.title}</h3>
        <p className="text-sm text-white/80">{module.description}</p>
      </div>
    </div>
    
    <div className="mt-4 flex items-center text-sm font-medium text-white/90 group-hover:text-white">
      <span>Abrir</span>
      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </Link>
);

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const [planInfo, setPlanInfo] = useState(null);
  const [showCreditsPurchase, setShowCreditsPurchase] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlanInfo = async () => {
      try {
        await refreshUser();
        const response = await plansAPI.getCurrentPlan();
        setPlanInfo(response.data);
      } catch (err) {
        setError('Error al cargar información del plan');
        console.error('Error fetching plan info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlanInfo();
  }, [refreshUser]);

  const handleCreditsPurchase = async (amount) => {
    try {
      const response = await plansAPI.purchaseCredits({
        amount: amount,
        payment_method: 'stripe'
      });
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      setError('Error al procesar la compra de créditos');
    }
  };

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.plan === 'admin' || 
    ['jorge.daza@gmail.com', 'verayerka239@gmail.com', 'jorge.daza039@gmail.com'].includes(user?.email);

  // ACTIVE modules only - organized by category
  const contentTools = [
    { 
      icon: '📝', 
      title: 'Generar Contenido', 
      link: '/generate', 
      description: 'Texto e imágenes con IA',
      gradient: 'from-blue-500 to-blue-600'
    },
  ];

  const assistants = [
    { 
      icon: '💬', 
      title: 'Chat IA', 
      link: '/chat', 
      description: 'Conversación inteligente',
      gradient: 'from-green-500 to-emerald-600'
    },
    { 
      icon: '🤖', 
      title: 'Chatbot Automatizado', 
      link: '/autochatbot', 
      description: 'IA multi-modelo avanzada',
      gradient: 'from-cyan-500 to-blue-600',
      badge: 'Avanzado'
    },
  ];

  const professionalTools = [
    { 
      icon: '💼', 
      title: 'Simulador Financiero', 
      link: '/accounting', 
      description: 'Plantillas y cálculos tributarios',
      gradient: 'from-emerald-500 to-green-600'
    },
    { 
      icon: '⚖️', 
      title: 'Asistente Legal', 
      link: '/legal', 
      description: 'Información legal orientativa',
      gradient: 'from-purple-500 to-violet-600'
    },
    { 
      icon: '🛡️', 
      title: 'Ciberseguridad', 
      link: '/cybersecurity', 
      description: 'Herramientas de protección',
      gradient: 'from-red-500 to-rose-600',
      badge: 'Descargable'
    },
  ];

  return (
    <div className="font-display bg-gray-50 dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        
        <main className="py-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  Hola, {user?.name?.split(' ')[0]}
                </h1>
                <p className="mt-1 text-gray-500 dark:text-gray-400">
                  Plan <span className="font-medium capitalize text-gray-700 dark:text-gray-300">{user?.plan}</span>
                </p>
              </div>
              
              {/* Credits Card */}
              <div className="flex items-center gap-4">
                <div className="bg-white dark:bg-[#1a2530] rounded-xl px-5 py-3 shadow-sm border border-gray-100 dark:border-[#283039]">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Créditos disponibles</p>
                      <p className="text-2xl font-bold text-primary">
                        {planInfo?.credits_remaining?.toLocaleString() || user?.credits?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowCreditsPurchase(!showCreditsPurchase)}
                      className="px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      Comprar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Panel Link */}
          {isAdmin && (
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <Link
                to="/admin/stats"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">📊</span>
                    <div>
                      <h3 className="font-bold">Estadísticas</h3>
                      <p className="text-sm text-white/80">Usuarios, referidos, actividad</p>
                    </div>
                  </div>
                  <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
              <Link
                to="/admin/invitations"
                className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">🎟️</span>
                    <div>
                      <h3 className="font-bold">Códigos de Invitación</h3>
                      <p className="text-sm text-white/80">Gestionar invitaciones</p>
                    </div>
                  </div>
                  <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </div>
          )}

          {/* Referral Program Banner */}
          <Link
            to="/referrals"
            className="block mb-8 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-3xl">🎁</span>
                <div>
                  <h3 className="font-bold">Programa de Referidos</h3>
                  <p className="text-sm text-white/80">Invita amigos y gana créditos gratis</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="hidden sm:block px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  500 créditos por referido
                </span>
                <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>

          {/* Credits Purchase Section */}
          {showCreditsPurchase && (
            <div className="mb-8 bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-[#283039]">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Comprar Créditos</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { amount: 1000, price: '$9.99', popular: false },
                  { amount: 5000, price: '$39.99', popular: true },
                  { amount: 15000, price: '$99.99', popular: false }
                ].map((option) => (
                  <button
                    key={option.amount}
                    onClick={() => handleCreditsPurchase(option.amount)}
                    className={`relative p-5 rounded-xl border-2 transition-all hover:border-primary ${
                      option.popular 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 dark:border-[#283039] hover:bg-gray-50 dark:hover:bg-[#283039]'
                    }`}
                  >
                    {option.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-white text-xs font-semibold rounded-full">
                        Popular
                      </span>
                    )}
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {option.amount.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">créditos</p>
                    <p className="mt-2 text-lg font-semibold text-primary">{option.price}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content Generation Tools */}
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Herramientas de Contenido
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contentTools.map((module) => (
                <ModuleCard key={module.title} module={module} />
              ))}
            </div>
          </section>

          {/* AI Assistants */}
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Asistentes IA
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assistants.map((module) => (
                <ModuleCard key={module.title} module={module} />
              ))}
            </div>
          </section>

          {/* Professional Tools */}
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Herramientas Profesionales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {professionalTools.map((module) => (
                <ModuleCard key={module.title} module={module} />
              ))}
            </div>
          </section>

          {/* Quick Access to Plans */}
          <section className="mt-12">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-[#1a2530] dark:to-[#283039] rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h3 className="text-xl font-bold text-white mb-1">
                    ¿Necesitas más capacidad?
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Explora nuestros planes y obtén más créditos y funciones
                  </p>
                </div>
                <Link
                  to="/pricing"
                  className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap"
                >
                  Ver planes
                </Link>
              </div>
            </div>
          </section>

          {error && (
            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-sm">
              {error}
            </div>
          )}
        </main>
        
        <Footer />
      </div>
    </div>
  );
};

export default Dashboard;
